/**
 * Sound Map Screen (route `SoundMap`)
 *
 * The learner's per-phoneme mastery map over the full target-language
 * inventory: header summary (state counts + mastered/total progress),
 * Consonants / Vowels / Diphthongs wrap-grids of PhonemeChip, and a
 * detail sheet per sound with a "practice this sound" action that opens
 * the pronunciation coach pre-targeted on that sound (focusPhoneme rides
 * session creation).
 *
 * States render straight from the server's `state` field — no
 * client-side threshold logic (see docs/specs/api/sound-map.api.md).
 *
 * @see docs/specs/mobile/sound-map.mobile.md
 */

import React, { useCallback, useMemo, useState } from 'react';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';

import { ScreenHeader } from '../components/ui/ScreenHeader';
import { WebContainer } from '../components/ui/WebContainer';
import { ErrorState, LoadingState } from '../components/ui';
import { FS_XS, FS_SM, FS_MD } from '../theme';
import { useDisplayTheme } from '../hooks/useDisplayTheme';
import { errorMessage } from '../generated/api/endpoint-errors';
import {
  useSoundMapStore,
  selectSoundMapData,
  selectSoundMapLoading,
  selectSoundMapErrorInfo,
  selectLoadSoundMap,
  selectClearSoundMapError,
} from '../store/soundMapStore';
import { useConversationStore } from '../store/conversationStore';
import { selectStartNewSession } from '../store/conversationSelectors';
import { PhonemeChip } from '../components/soundmap/PhonemeChip';
import { PhonemeDetailSheet } from '../components/soundmap/PhonemeDetailSheet';
import { formatSummaryLine, showsProgress, translateSectionTitle, type PhonemeType } from '../components/soundmap/soundMapText';
import type { SoundMapPhoneme } from '../generated/api/http-contracts';

/** Render order for sections — matches the server's inventory ordering. */
const SECTION_ORDER: PhonemeType[] = ['consonant', 'vowel', 'diphthong', 'tone'];

export function SoundMapScreen() {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();
  const navigation = useNavigation();

  const data = useSoundMapStore(selectSoundMapData);
  const loading = useSoundMapStore(selectSoundMapLoading);
  const errorInfo = useSoundMapStore(selectSoundMapErrorInfo);
  const loadSoundMap = useSoundMapStore(selectLoadSoundMap);
  const clearError = useSoundMapStore(selectClearSoundMapError);
  const startNewSession = useConversationStore(selectStartNewSession);

  const [selectedPhoneme, setSelectedPhoneme] = useState<SoundMapPhoneme | null>(null);
  const [isStartingPractice, setIsStartingPractice] = useState(false);

  // Load on focus — standard refetch-on-view pattern (no local persistence in v1).
  useFocusEffect(
    useCallback(() => {
      void loadSoundMap();
    }, [loadSoundMap]),
  );

  const handleRetry = useCallback(() => {
    clearError();
    void loadSoundMap();
  }, [clearError, loadSoundMap]);

  const handleChipPress = useCallback((phoneme: SoundMapPhoneme) => {
    setSelectedPhoneme(phoneme);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setSelectedPhoneme(null);
  }, []);

  // Open the pronunciation coach pre-targeted on the tapped sound: the
  // ipaSymbol rides session creation as `focusPhoneme` and becomes the
  // Learner-Chosen Focus block in the coach's first prompt (see
  // docs/specs/api/sound-map.api.md § Practice pre-targeting). Falls back
  // to a plain session when no phoneme is selected.
  const handlePractice = useCallback(async () => {
    if (isStartingPractice) return;
    setIsStartingPractice(true);
    try {
      await startNewSession(
        'pronunciation',
        selectedPhoneme ? { focusPhoneme: selectedPhoneme.ipaSymbol } : undefined,
      );
      const sessionId = useConversationStore.getState().currentSessionId;
      if (sessionId) {
        setSelectedPhoneme(null);
        navigation.navigate('Chat', { sessionId });
      }
    } finally {
      setIsStartingPractice(false);
    }
  }, [isStartingPractice, startNewSession, navigation, selectedPhoneme]);

  // Group by type, preserving the server's per-group ordering.
  const sections = useMemo(() => {
    if (!data) return [];
    return SECTION_ORDER.map((type) => ({
      type,
      phonemes: data.phonemes.filter((p) => p.type === type),
    })).filter((section) => section.phonemes.length > 0);
  }, [data]);

  const summaryLine = data ? formatSummaryLine(t, data) : '';
  const total = data ? data.phonemes.length : 0;
  const mastered = data ? data.summary.mastered : 0;
  const progressPct = total > 0 ? Math.min(100, (mastered / total) * 100) : 0;
  // No progress dimension for this target variant: the bar could never move, so
  // the card explains what the map DOES offer instead of showing an empty one
  // (TCP-006 — a blocked capability is never silently absent).
  const hasProgress = data ? showsProgress(data) : true;

  return (
    <View testID="sound-map-screen" style={{ flex: 1, backgroundColor: theme.background }}>
      <ScreenHeader title={t('soundMap.title')} />
      <WebContainer variant="page" style={{ flex: 1 }}>
        {!data && loading && (
          <LoadingState testID="sound-map-loading" message={t('soundMap.loading')} />
        )}

        {!data && !loading && errorInfo && (
          <ErrorState
            testID="sound-map-error"
            message={errorMessage(errorInfo) || t('soundMap.loadError')}
            retryLabel={t('common.retry')}
            onRetry={handleRetry}
          />
        )}

        {data && (
          <ScrollView contentContainerStyle={styles.scrollContent}>
            {/* ── Header summary ── */}
            <View
              testID="sound-map-summary"
              style={[styles.summaryCard, { backgroundColor: theme.card, borderColor: theme.border }]}
            >
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_SM * fontScale, color: theme.text, ...ffs }}
              >
                {summaryLine}
              </Text>
              {hasProgress ? (
                <View style={styles.progressRow}>
                  <View style={[styles.progressTrack, { backgroundColor: theme.border }]}>
                    <View
                      style={[
                        styles.progressFill,
                        { backgroundColor: theme.success, width: `${progressPct}%` },
                      ]}
                    />
                  </View>
                  <Text
                    allowFontScaling={false}
                    style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, marginLeft: 8, ...ffs }}
                  >
                    {t('soundMap.progressLabel', { mastered, total })}
                  </Text>
                </View>
              ) : (
                <Text
                  testID="sound-map-guidance-note"
                  allowFontScaling={false}
                  style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, marginTop: 6, ...ffs }}
                >
                  {t('soundMap.guidanceOnly.body')}
                </Text>
              )}
            </View>

            {/* ── Sections ── */}
            {sections.map((section) => (
              <View key={section.type} testID={`sound-map-section-${section.type}`}>
                <Text
                  allowFontScaling={false}
                  accessibilityRole="header"
                  style={{
                    fontSize: FS_MD * fontScale,
                    fontWeight: '700',
                    color: theme.text,
                    marginTop: 20,
                    marginBottom: 10,
                    ...ffs,
                  }}
                >
                  {translateSectionTitle(t, section.type)}
                </Text>
                <View style={styles.grid}>
                  {section.phonemes.map((phoneme) => (
                    <PhonemeChip key={phoneme.ipaSymbol} phoneme={phoneme} onPress={handleChipPress} />
                  ))}
                </View>
              </View>
            ))}
          </ScrollView>
        )}
      </WebContainer>

      {selectedPhoneme && (
        <PhonemeDetailSheet
          phoneme={selectedPhoneme}
          visible
          onClose={handleCloseDetail}
          onPractice={handlePractice}
          practiceLoading={isStartingPractice}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  scrollContent: {
    padding: 16,
    paddingBottom: 32,
  },
  summaryCard: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 14,
  },
  progressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 10,
  },
  progressTrack: {
    flex: 1,
    height: 6,
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 3,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
});
